import type { ReactNode } from 'react'
import React from 'react'
import { getWindow, hasWindow } from '@ztechapps/common.utils'
import styles from './SpreadsheetCellShell.module.scss'

/**
 * Spreadsheet cell `<td>` wrapper. Two render modes:
 *   - VIEW: cell is focusable (`tabIndex={0}` when active, `-1` otherwise). Renders `children`
 *     (typically the formatted value).
 *   - EDIT: same `<td>` shell, renders the editable input as `children`. The input takes DOM
 *     focus on its own (react-select auto-focuses; `TextInput` gets a ref-based focus).
 *
 * The effect re-runs on `isActive` AND `isEditing` so the `<td>` re-claims focus when an editing
 * input unmounts (commit / revert). Without `isEditing` in the deps, focus would fall to `<body>`
 * after a commit and subsequent keypresses would never reach the spreadsheet container handler.
 * If focus is already inside the cell (the inner input has it during edit mode), the effect is a
 * no-op — it does not steal focus from the react-select input.
 *
 * The active cell draws its whole ring (border + offset outline) from the SCSS module rather than
 * Bootstrap's `border-primary` utility: that utility is `!important` on the fill token, which is
 * fixed across colour modes and would pin the border to one colour while the outline tracked the
 * theme. `align-middle` centers content vertically so view-mode text aligns with taller edit-mode
 * inputs in the same row.
 */
export type SpreadsheetCellShellProps = {
  isActive: boolean
  isEditing?: boolean
  tabIndex?: number
  className?: string
  children: ReactNode
}

export const SpreadsheetCellShell = ({
  isActive,
  isEditing = false,
  tabIndex,
  className,
  children,
}: SpreadsheetCellShellProps) => {
  const tdRef = React.useRef<HTMLTableCellElement>(null)
  const baseClass = isActive ? `${styles.activeCell} align-middle` : 'border align-middle'
  const fullClass = className ? `${baseClass} ${className}` : baseClass

  React.useEffect(() => {
    if (!isActive) return
    const td = tdRef.current
    if (!td || !hasWindow()) return
    const win = getWindow()
    if (!win) return
    if (td.contains(win.document.activeElement)) return
    td.focus()
  }, [isActive, isEditing])

  return (
    <td ref={tdRef} tabIndex={tabIndex ?? -1} className={fullClass}>
      {children}
    </td>
  )
}
